'use client';

import Icon from '@/components/ui/AppIcon';

interface CommissionStatsProps {
  dateRange: string;
}

const CommissionStats = ({ dateRange }: CommissionStatsProps) => {
  // Demo data based on date range
  const stats = {
    totalEarnings: dateRange === '7days' ? 342.50 : dateRange === '30days' ? 1456.80 : dateRange === '90days' ? 4823.40 : 12567.90,
    pendingCommission: dateRange === '7days' ? 89.20 : dateRange === '30days' ? 423.60 : dateRange === '90days' ? 1234.50 : 3456.70,
    totalBookings: dateRange === '7days' ? 23 : dateRange === '30days' ? 87 : dateRange === '90days' ? 256 : 678,
    conversionRate: dateRange === '7days' ? 3.2 : dateRange === '30days' ? 4.5 : dateRange === '90days' ? 5.1 : 6.3,
  };

  const statCards = [
    {
      label: 'Total Earnings',
      value: `£${stats.totalEarnings.toFixed(2)}`,
      icon: 'BanknotesIcon',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      change: '+12.5%',
      changeType: 'positive' as const,
    },
    {
      label: 'Pending Commission',
      value: `£${stats.pendingCommission.toFixed(2)}`,
      icon: 'ClockIcon',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      change: '+8.3%',
      changeType: 'positive' as const,
    },
    {
      label: 'Total Bookings',
      value: stats.totalBookings.toString(),
      icon: 'TicketIcon',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      change: '+15.7%',
      changeType: 'positive' as const,
    },
    {
      label: 'Conversion Rate',
      value: `${stats.conversionRate}%`,
      icon: 'ChartBarIcon',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      change: '+2.1%',
      changeType: 'positive' as const,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-white border border-border rounded-lg p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <Icon name={stat.icon as any} size={24} className={stat.color} />
              </div>
              <span
                className={`text-xs font-bold px-2 py-1 rounded ${
                  stat.changeType === 'positive' ?'bg-green-100 text-green-700' :'bg-red-100 text-red-700'
                }`}
              >
                {stat.change}
              </span>
            </div>
            <h3 className="text-sm text-muted-foreground mb-1">{stat.label}</h3>
            <p className="text-2xl font-black text-foreground">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Partner Breakdown */}
      <div className="bg-white border border-border rounded-lg p-6">
        <h3 className="font-bold text-lg text-foreground mb-4">Earnings by Partner</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Icon name="GlobeAltIcon" size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="font-bold text-foreground">Skyscanner</p>
                <p className="text-xs text-muted-foreground">Flight bookings</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-black text-foreground">£{(stats.totalEarnings * 0.45).toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">{Math.round(stats.totalBookings * 0.4)} bookings</p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <Icon name="BuildingOffice2Icon" size={20} className="text-green-600" />
              </div>
              <div>
                <p className="font-bold text-foreground">Booking.com</p>
                <p className="text-xs text-muted-foreground">Hotel bookings</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-black text-foreground">£{(stats.totalEarnings * 0.35).toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">{Math.round(stats.totalBookings * 0.35)} bookings</p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <Icon name="SparklesIcon" size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="font-bold text-foreground">Expedia</p>
                <p className="text-xs text-muted-foreground">Holiday packages</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-black text-foreground">£{(stats.totalEarnings * 0.20).toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">{Math.round(stats.totalBookings * 0.25)} bookings</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommissionStats;
